import { Component, Element, Method, Prop, Event, EventEmitter, h, Watch } from '@stencil/core';
import { whichTransitionEvent } from '../../utils/functions';

const transitionEvent = whichTransitionEvent();

/**
 * A collapse section simplifies the user interface by condensing long content into collapsible content sections.
 */
@Component({
  tag: 'som-collapse',
  styleUrl: 'som-collapse.scss',
})
export class SomCollapse {
  @Element() el: HTMLElement;

  /**
   * This boolean property that sets the initial state of the collapse element.
   */
  @Prop() collapsed: boolean = false;

  /**
   * Event triggered anytime the component is toggled.
   * @Returns `event.detail` will contain the state of the collapse as a `boolean` value.
   */
  @Event() collapseToggled: EventEmitter;

  containerElement; // The element where the transition animation occurs.

  contentElement;

  cancelCallback; // Callback used when a transition is cancelled.

  opened; // Keeps track of whether the collapse is opened or not.

  height; // Keeps track of current height. Used for (re)rendering.

  hidden; // Keeps track of whether content should be hidden. Used for (re)rendering.

  overflowHidden; // Keeps track of whether content overflow should be hidden. Used for (re)rendering.

  /**
   * This method toggles the collapse content. Toggle will show content if it is currently hidden or hide content if it is currently visibile.
   * @returns Promise will resolve after the toggle animation ends.
   */
  @Method()
  toggle(): Promise<void> {
    return this.toggleCollapse(!this.opened);
  }

  /**
   * This method shows the collapse content regardless of the current state.
   * @returns Promise will resolve after the show animation ends.
   */
  @Method()
  show(): Promise<void> {
    return this.toggleCollapse(true);
  }

  /**
   * This method hides content regardless of the current state.
   * @returns Promise will resolve after the hide animation ends.
   */

  @Method()
  hide(): Promise<void> {
    return this.toggleCollapse(false);
  }

  /**
   * DEPRECATED - This method was used to recalculate and update the tab content height.
   * We do not need to call this method anymore since content height is automatically updated internally.
   *
   * @returns Promise
   */
  @Method()
  refresh(): Promise<void> {
    return Promise.resolve();
  }

  @Watch('collapsed')
  watchCollapsed(newVal, oldVal) {
    newVal != oldVal && this.toggleCollapse(oldVal);
  }

  open(): Promise<void> {
    if (this.cancelCallback) {
      // Make sure to cancel current promise
      this.cancelCallback();
    }

    return new Promise((resolve, reject) => {
      this.opened = true;

      this.setOverflowHidden(true);
      this.setHidden(false);

      // Set container's height to content height for animation to start from its current height to new height.
      this.setHeight(`${this.contentElement.scrollHeight}px`);

      this.listenForTransitionEnd(err => {
        // Remove the cancel callback since the transition is completed.
        this.cancelCallback = null;

        if (err) {
          // An error occured (i.e. `close()` was called before `open()` could finish)
          return reject(err);
        }

        // Transition is done and collapse is opened
        this.setOverflowHidden(false);

        // Remove the height used for animation to make sure that collapse's height will adjust if content changes.
        this.setHeight(null);

        // Trigger the collapse event and resolve.
        this.collapseToggled.emit('true');
        return resolve();
      });
    });
  }

  close(): Promise<void> {
    if (this.cancelCallback) {
      // Make sure to cancel current promise
      this.cancelCallback();
    }

    return new Promise((resolve, reject) => {
      this.opened = false;

      // Disable overflow when closing since container height will shrink.
      this.setOverflowHidden(true);

      // We set the current height in pixel so we can animate.
      this.setHeight(`${this.contentElement.scrollHeight}px`);

      requestAnimationFrame(() => {
        // When this code is reached, we are in the Event Loop just before the `px` height style is applied.

        // We want the `px` height style to be applied and then have the height be set to `0px` to start the transition animation. This can only be done on next frame.
        requestAnimationFrame(() => {
          // When this code is reached, the `px` height has already been applied. We can set the new height to `0px` to launch the transition animation.
          this.setHeight('0px');

          this.listenForTransitionEnd(err => {
            // Remove the cancel callback since the transition is completed.
            this.cancelCallback = null;

            if (err) {
              // An error occured (i.e. `open()` was called before `close()` could finish)
              return reject(err);
            }

            // Hide the content to prevent tabbing to elements visually hidden.
            this.setHidden(true);

            // The collapse was succesfully closed.
            this.collapseToggled.emit('false');

            return resolve();
          });
        });
      });
    });
  }

  setHeight(height) {
    this.containerElement.style.height = height;
    this.height = height;
  }

  setOverflowHidden(overflowHidden) {
    if (overflowHidden) {
      this.containerElement.classList.add('overflow-hidden');
    } else {
      this.containerElement.classList.remove('overflow-hidden');
    }
    this.overflowHidden = overflowHidden;
  }

  setHidden(hidden) {
    if (hidden) {
      this.contentElement.classList.add('display-none');
    } else {
      this.contentElement.classList.remove('display-none');
    }
    this.hidden = hidden;
  }

  toggleCollapse(show): Promise<void> {
    if (!this.opened && show) {
      // Collapse is closed and we want to open it.
      return this.open();
    }

    if (this.opened && !show) {
      // Collapse is opened and we want to close it
      return this.close();
    }

    return Promise.resolve();
  }

  listenForTransitionEnd(callback) {
    const elem = this.containerElement;
    if (transitionEvent) {
      let onTransitionCompletedCb;

      const onTransitionCompleted = event => {
        // We are only reacting to transitions on 'height'
        if (event.propertyName === 'height') {
          // Transition is completed
          elem.removeEventListener(transitionEvent, onTransitionCompletedCb);
          callback();
        }
      };
      onTransitionCompletedCb = onTransitionCompleted.bind(this);
      elem.addEventListener(transitionEvent, onTransitionCompletedCb);

      this.cancelCallback = () => {
        // Remove cancel callback since it was just called
        this.cancelCallback = null;

        // Remove listeners
        elem.removeEventListener(transitionEvent, onTransitionCompletedCb);

        callback('Cancelled');
      };
    }
  }

  componentWillLoad() {
    this.opened = !this.collapsed;

    if (this.opened) {
      this.overflowHidden = false;
    } else {
      this.height = '0px';
      this.hidden = true;
    }
  }

  componentDidLoad() {
    this.containerElement = this.el.querySelector('.collapse-content-container') as HTMLDivElement;
    this.contentElement = this.el.querySelector('.collapse-content') as HTMLDivElement;
  }

  render() {
    return (
      <div class={`collapse-content-container ${this.overflowHidden ? 'overflow-hidden' : ''}`} style={{ height: this.height }}>
        <div class={`collapse-content ${this.hidden ? 'display-none' : ''}`}>
          <slot />
        </div>
      </div>
    );
  }
}
